import {
  DefaultEcoLifeField,
  EcoLife,
  SubjectiveCharacteristic,
  noop,
} from '@aloft/toolkit-shared';
import { Block, Flex, IconButton, SimpleInput, SimpleRange, Typography } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { Popover, PopoverContent, PopoverTrigger, Portal } from '@aloft/toolkit-sui/chakra';

import { GuidedTourTooltip } from '../../../../components/GuidedTour/GuidedTourTooltip.tsx';
import { setTotalEconomicLife } from '../../../../redux/features/actions/setTotalEconomicLife.ts';
import { sliderAdjustment } from '../../../../redux/features/actions/sliderAdjustment.ts';
import {
  selectEcoLifeByIdAndField,
  selectSubjectiveCharacteristicsByIdAndField,
} from '../../../../redux/features/selectors/propertyDataSelectors/improvementsSelectors.ts';
import { selectFirstOrFewerCompID } from '../../../../redux/features/selectors/selectFirstOrFewerCompID.ts';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks.ts';
import { RootState } from '../../../../redux/types.ts';

const MIN = 0;

type InputsProps = {
  id: string;
  index: number;
  effectiveAgeField: SubjectiveCharacteristic;
  ecoLifeField: EcoLife;
  defaultEcoLifeField: DefaultEcoLifeField;
  label?: string;
  enabled: boolean;
};

export const EffectiveAgeSliderInputs = ({
  id,
  index,
  effectiveAgeField,
  ecoLifeField,
  defaultEcoLifeField,
  label,
  enabled,
}: InputsProps) => {
  const dispatch = useAppDispatch();
  const effectiveAgeValue =
    useAppSelector((state: RootState) =>
      selectSubjectiveCharacteristicsByIdAndField(state, id, effectiveAgeField),
    ) ?? 0;
  const secondOrFewerCompID = useAppSelector(selectFirstOrFewerCompID);
  const handleEffectiveAgeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      sliderAdjustment({
        id,
        field: effectiveAgeField,
        newValue: Number(event.target.value),
      }),
    );
  };

  const totalEconomicLifeValue =
    useAppSelector((state: RootState) =>
      selectEcoLifeByIdAndField(state, id, ecoLifeField, defaultEcoLifeField),
    ) ?? 0;

  const handleTotalEconomicLifeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      setTotalEconomicLife({ id, field: ecoLifeField, newValue: Number(event.target.value) }),
    );
  };

  return (
    <Flex flexDirection="column">
      <Flex justifyContent="space-between" alignItems="center">
        <Block>
          <Typography variant="h4" color="textSecondary">
            {label ?? ''}
          </Typography>
        </Block>
        <Flex gap=".5rem" alignItems="center">
          <Typography variant="subtitle3" color="textSecondary">
            Econ. Life {totalEconomicLifeValue.toString()}
          </Typography>
          <Popover placement="top">
            {({ onClose, isOpen }) => (
              <>
                <PopoverTrigger>
                  <IconButton
                    size="xs"
                    ariaLabel="Set Total Economic Life"
                    icon="pencil"
                    variant="tertiary"
                    iconColor="textSecondary"
                    onClick={isOpen ? onClose : noop}
                  />
                </PopoverTrigger>
                <Portal>
                  <PopoverContent width="fit-content" zIndex={3}>
                    <Flex
                      gap="1rem"
                      width="100%"
                      justifyContent="flex-start"
                      alignItems="center"
                      padding="1rem"
                    >
                      <SimpleInput
                        label="Total Economic Life"
                        type="number"
                        width="10rem"
                        value={totalEconomicLifeValue?.toString()}
                        onBlur={handleTotalEconomicLifeChange}
                        min={1}
                      />
                    </Flex>
                  </PopoverContent>
                </Portal>
              </>
            )}
          </Popover>
        </Flex>
      </Flex>
      <GuidedTourTooltip
        text="Then use the actual age and photos of the property to estimate the effective age of each property."
        tourID="analysisTour"
        tourIndex={5}
        displayCondition={id === secondOrFewerCompID && index === 0}
        placement="top"
      >
        <Flex gap="1rem" width="100%" justifyContent="space-between" alignItems="center">
          <SimpleRange
            value={effectiveAgeValue.toString()}
            min={MIN}
            max={totalEconomicLifeValue}
            onChange={handleEffectiveAgeChange}
            disabled={!enabled}
          />

          <SimpleInput
            type="number"
            min={MIN}
            max={totalEconomicLifeValue}
            value={effectiveAgeValue.toString()}
            onChange={handleEffectiveAgeChange}
            size="sm"
            width="4rem"
            disabled={!enabled}
          />
        </Flex>
      </GuidedTourTooltip>
    </Flex>
  );
};
